/*
    AppState.js -- Global state
 */

import {allow} from '@/paks/js-polyfill'
import blend from '@/paks/js-blend'
import Expression from '@/paks/js-expression'
import {Log} from '@/paks/js-log'
import {State} from '@/paks/vu-state'
import {Routes} from '@/paks/vu-routes'
import {Account} from '@/paks/mgr-models'
import {clone, getVuetify} from '@/paks/vu-app'

const PersistState = ['brightMode', 'dark', 'prefers', 'signs', 'upgradeAttempt']

export default class AppState {
    api = null
    brightMode = null
    closing = null
    cognito = null
    context = {}
    dark = null
    display = null                  // Reference into fullDisplay.displays[]
    fullDisplay = null
    help = null
    lastAccess = new Date()
    local = false
    location = {
        hash: null,
        query: {},
        prior: null,
        url: null,
    }
    logging = null
    logo = null
    manager = null
    mobile = false
    needs = {
        repaint: null,
        reload: null,
    }
    nextTable = 1
    prefers = {}
    refresh = null
    schema = null
    signs = {}
    social = null
    started = new Date()
    standardDisplay = null
    title = null
    theme = {}
    unsaved = null
    upgrading = false
    upgradeAttempt = 0
    versions = {}

    saveState(state) {
        return allow(state, PersistState)
    }

    loadState(state) {
        state = allow(state, PersistState)
        state.context = blend(state.context, {
            desktop: window.innerWidth >= 640,
            mobile: window.innerWidth < 640,
            agent: navigator.userAgent,
            height: window.innerWidth,
            language: navigator.language,
            width: window.innerWidth,
        })
        return state
    }

    get display() {
        return this.display
    }

    async closeAccount() {
        try {
            this.closing = true
            await Account.close()
        } catch (err) {
            Log.error('Cannot close account', {err})
        } finally {
            this.closing = false
        }
    }

    dismissSign(name) {
        this.signs[name] = this.signs[name] || {}
        this.signs[name].dismiss = true
    }

    getCart() { return [] }

    getInvite() {
        return this.location.query.invite
    }

    getSign(name) {
        return (this.signs[name] = this.signs[name] || {})
    }

    getNextTable() {
        return this.nextTable++
    }

    get isSocial() {
        let loc = State.app.location || '/'
        return loc.url.indexOf('/login/social') == 0 ? true : false
    }

    need(key) {
        return this.needs[key]
    }

    noNeed(key) {
        delete this.needs[key]
    }

    prefer(key, value) {
        return this.prefers[key]
    }

    setActivity() {
        this.lastAccess = new Date()
    }

    setApi(api) {
        if (!api) {
            api = `${window.location.host}/api`
        }
        this.api = api
    }

    setCognito(cognito) {
        this.cognito = cognito
    }

    setStandardDisplay(display) {
        this.standardDisplay = clone(display)
        for (let display of this.standardDisplay.displays) {
            this.rebaseViews(display)
        }
    }

    setFullDisplay(fullDisplay) {
        this.fullDisplay = fullDisplay
        let display = this.pickDisplay(fullDisplay)
        this.setDisplay(display)
    }

    setDisplay(display) {
        this.display = display
        if (display.features) {
            this.setFeatures(display.features)
        }
        if (display.logo) {
            this.setLogo(display.logo)
        }
        if (display.themes) {
            this.setThemes(display.themes)
        }
        if (display.title) {
            this.setTitle(display.title)
        }
        if (display.version) {
            State.config.version = display.version
        }
        if (display.timeouts) {
            State.config.timeouts = Object.assign(State.config.timeouts || {}, display.timeouts)
        }
        this.mobile = window.innerWidth <= 640
        this.rebaseViews(display)
        Routes.replaceRoutes(display)
        State.app.setNeed('repaint')
        Log.info(`Selected display "${display.name}" using "${display.expression}"`)
    }

    setManager(manager) {
        this.manager = manager
    }

    rebaseViews(parent) {
        let views = (parent.views || []).concat(parent.children || [])
        for (let view of views) {
            view.path = this.rebase(view.path, parent)
            if (view.redirect) {
                view.redirect = this.rebase(view.redirect, view)
            }
            if (view.views || view.children) {
                this.rebaseViews(view)
            }
        }
    }

    rebase(path, parent) {
        if (!path || path[0] == '/' || !parent) {
            return path
        }
        if (parent.path == '/') {
            return `/${path}`
        } else {
            return `${parent.path}/${path}`
        }
    }

    setFeatures(features) {
        State.config.features = blend(State.config.features || {}, features)
        if (State.config.features?.auth?.social) {
            this.setSocial(State.config.features.auth.social)
        }
    }

    setInvite(value) {
        return (this.location.query.invite = value)
    }

    setHelp() {}

    setLogo(logo) {
        this.logo = logo
    }

    setNeed(key, value) {
        if (value == undefined) {
            value = Date.now()
        }
        this.needs[key] = value
    }

    setPrefer(key, value) {
        this.prefers[key] = value
    }

    setSchema(schema) {
        this.schema = schema
    }

    setSocial(social) {
        this.social = social
    }

    setBrightMode(mode) {
        mode = State.app.brightMode = mode || State.app.brightMode || 'auto'
        if (!mode || mode == 'auto') {
            mode = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
        }
        getVuetify().theme.global.name.value = mode
        State.app.dark = mode == 'dark'

        let themes = State.config.themes
        this.theme = themes.modes[mode]
        this.setLogo(this.theme.logo || themes.logo || '/images/logo.png')
    }

    setThemes(themes) {
        if (!themes.formats) {
            themes.formats = State.config.formats
        }
        if (!themes.login) {
            themes.login = State.config.login
        }
        State.config.themes = themes
        let vthemes = getVuetify().theme.themes.value
        for (let mode of Object.keys(themes.modes)) {
            vthemes[mode].colors = themes.modes[mode].colors
            Object.assign(vthemes[mode].variables, themes.modes[mode].variables)
        }
        this.setBrightMode()
    }

    setTitle(title) {
        this.title = title
    }

    setVersions() {}

    setContext(key, value) {
        if (typeof key == 'object') {
            for (let [k, v] of Object.entries(key)) {
                if (k != 'id') {
                    this.context[k] = v
                }
            }
        } else if (key != 'id') {
            this.context[key] = value
        }
    }

    /*
        Test a value against a pattern with a default value if not defined
        Return true if the value matches the pattern
        Properties evaluated as:
            Number
            "string"
            /RegExp/
            "OP string" where OP is < <= >= > == !=
        }
        Group expression elements with:
            [ ] -- Return true if all are true
            { } -- Return true if any are true
        Returns true if any (or) of the values are true
        Return true if expression is not defined
     */
    testContext(value, pattern, defaultValue = true) {
        if (value == null) {
            return defaultValue
        }
        /*
        if (value === true) {
            return true
        }
        if (value === false) {
            return false
        } */
        if (value == pattern) {
            return true
        }
        /*
        if (typeof pattern == 'string') {
            let expression = new Expression({debug: false})
            let ast = expression.parse(display.expression || true)
            enable = await expression.run(ast, this.context, 50)
        }
         */
        if (typeof pattern == 'string') {
            if (pattern[0] == '/' && pattern[pattern.length -1] == '/') {
                pattern = pattern.slice(1, -1)
                return new RegExp(pattern).test(value) ? true : false
            } else {
                let [op, pat] = pattern.split(' ')
                if (op) {
                    switch (op) {
                    case '<': 
                        return value < pat
                    case '<=':
                        return value <= pat
                    case '>':
                        return value > pat
                    case '>=':
                        return value >= pat
                    case '==':
                        return value == pat
                    case '!=':
                        return value != pat
                    }
                }
            }
        }
        if (Array.isArray(value)) {
            //  Implements "AND" of all items. One false and we're out.
            for (let item of value) {
                if (!testContext(value, item, defaultValue)) {
                    return false
                }
            }
            return true
        } else if (typeof value == 'object' && Object.keys(value).length > 0) {
            //  Implements "OR" of values. One true and we're in.
            for (let [key, pat] of Object.entries(value)) {
                if (this.testContext(this.context[key], pat, defaultValue)) {
                    return true
                }
            }
            return false
        }
        return defaultValue
    }

    /* 
        Pick a display from the full display list
        If there is not displays[] list, then return the full display
        If no display matches, then return the first display
        If a display has no expression, then it is selected by default
     */
    pickDisplay(fullDisplay = this.fullDisplay) {
        if (fullDisplay.displays) {
            for (let display of fullDisplay.displays) {
                let enable = true
                if (display.expression != null) {
                    let expression = new Expression({debug: false})
                    let ast = expression.parse(display.expression || true)
                    /*
                        Example context:
                            agent: "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/136.0.0.0 Safari/537.36"
                            desktop: true
                            height: 1272
                            language: "en-GB"
                            mobile: false
                            width: 1272

                        width >= 640 && language == "en-GB"
                        width >= 640 && agent >< 'Mozilla'
                        desktop && agent == /Chrome/
                        mobile && language == /en/
                     */
                    let context = {
                        desktop: window.innerWidth >= 640,
                        mobile: window.innerWidth < 640,
                        agent: navigator.userAgent,
                        height: window.innerWidth,
                        language: navigator.language,
                        width: window.innerWidth,
                        timezone: new Date().getTimezoneOffset(),
                    }
                    enable = expression.run(ast, context, 50)
                }
                if (enable) {
                    return display
                }
            }
            console.log('No matching display found, picking first')
            return fullDisplay.displays[0]
        } else {
            console.log('Legacy display format')
            return fullDisplay
        }
    }
}